// zoom — 表示ズームの純粋ポリシー (定数 + clamp + ステップ + fit 計算)。
//
// M1-a 分数ズーム (docs/_wip/modern-phone-shell-2026-07-02.md §3.1):
//   - 従来 zoom は整数 1..16 のみ (旧 applet 踏襲、clampZoom が Math.trunc)。
//     390px 級スマホで 2000px 級 canvas の全景が表示できないため、**連続値
//     (分数) zoom** を許容し、下限を `ZOOM_FIT_MIN` (0.1x) まで開放する。
//   - `ZOOM_MIN` (= 1) の意味は壊さない: 「等倍 = リセット先」のまま。連続値の
//     下限は別名 `ZOOM_FIT_MIN` で持つ。
//
// 決定2 (consult 2026-09-29、modern-phone-shell-2026-07-02.md §6、**作者合意済みで
// 差し替え**): iPad の「ズームが使いづらい」FB を受け、UI ズームのステップを
// **CSP (Clip Studio Paint) 方式**に統一する。
//   - M1-a 時点の当初案 (1x 以上=整数ステップ非退行、1x 未満だけ別テーブル) は
//     不採用に変更。**1x 以上も含めて同一規則**にする。
//   - 1 操作 = 現在値 **× `ZOOM_STEP_RATIO` (1.1、拡大) / ÷ `ZOOM_STEP_RATIO`
//     (縮小)** の幾何級数。ただしその 1 手の間に `ZOOM_ANCHORS` の値を
//     またぐ (拡大: `z < anchor <= z*1.1`、縮小: `z > anchor >= z/1.1`) ときは
//     アンカーちょうどで止める。
//   - 例 (1x から拡大): `1 → 1.1 → 1.21 → 1.331 → 1.4641 → 1.5 (アンカーで停止)
//     → 1.65 → …`。
//   - ± ボタン / キーボード +/− / ホイールノッチいずれも `zoomStepIn`/
//     `zoomStepOut` を通るので、呼び出し側 (`useRoomChrome`) は無変更で新方式に
//     乗る。
//   - 1x 超の分数 (アンカー到達前の中間値) では従来どおり `imageRendering:
//     pixelated` のまま (= ピンチで既に分数ズームが出ている現状と同じ、
//     CanvasDrawingEngine 側の `zoom >= 1 ? 'pixelated' : 'auto'` は不変)。
//
// CanvasDrawingEngine から分離した理由: engine 本体 (巨大ファイル) と UI
// (useRoomChrome) の両方がこのポリシーを使うため、循環 import なしで共有できる
// 小さな純関数モジュールに切り出す (engine は ZOOM_MIN/ZOOM_MAX を re-export
// するので既存の import 先は不変)。

/**
 * zoom の下限側基準値 = 等倍 (リセット先)。`ZOOM_ANCHORS` の中間点でもある。
 * 連続値としての下限は `ZOOM_FIT_MIN` (別物、フィット/ピンチが到達しうる最小)。
 */
export const ZOOM_MIN = 1;

/** zoom の上限 (旧 applet 踏襲 16x)。`ZOOM_ANCHORS` の最大値と一致。 */
export const ZOOM_MAX = 16;

/**
 * 連続値 (分数) zoom の下限 (M1-a)。fit-to-viewport / ピンチが到達しうる最小
 * 倍率。0.1x = 2000px 級 canvas が 200px 相当まで縮む = 390px 級 viewport で
 * 全景 + 余白が確実に収まる (設計メモ §3.1 M1-a「固定下限なら ~0.1x」)。
 * `ZOOM_ANCHORS` の最小値と一致。
 */
export const ZOOM_FIT_MIN = 0.1;

/**
 * 連続値 zoom を `[ZOOM_FIT_MIN, ZOOM_MAX]` に clamp する。NaN / 非有限は
 * `ZOOM_MIN` (= 1、等倍) へ倒す (= engine が未定義状態に落ちない防御、旧
 * clampZoom と同じ流儀)。**Math.trunc はしない** (M1-a 分数ズーム)。
 */
export const clampZoom = (zoom: number): number => {
  if (!Number.isFinite(zoom)) return ZOOM_MIN;
  if (zoom < ZOOM_FIT_MIN) return ZOOM_FIT_MIN;
  if (zoom > ZOOM_MAX) return ZOOM_MAX;
  return zoom;
};

/**
 * CSP 方式ズームの 1 手あたりの乗率 (決定2 差し替え、consult 2026-09-29)。
 * `zoomStepIn` は `z * ZOOM_STEP_RATIO`、`zoomStepOut` は `z / ZOOM_STEP_RATIO`
 * が既定の移動量 (アンカーをまたがない限り)。
 */
export const ZOOM_STEP_RATIO = 1.1;

/**
 * ズームの「アンカー」(決定2、consult 2026-09-29)。幾何級数のステップが
 * またいだら、その手はアンカーちょうどで止める基準値。昇順で保持し、両端は
 * `ZOOM_FIT_MIN` / `ZOOM_MAX` と一致させてある (= clamp 境界とアンカーが
 * ずれない)。
 *
 * - 1x 未満: `ZOOM_FIT_MIN` (0.1) / 1/8 / 1/5 / 1/4 / 1/3 / 1/2 / 3/4 — 旧
 *   (当初案) 決定2 の刻みテーブルを補助アンカーとして踏襲 (0.25→0.1 の間が
 *   ×1.1 だけだと間延びしないように 1/8・1/5 を挟む)。
 * - 1x 以上: 1 / 1.5 / 2 / 3 / 4 / 6 / 8 / 12 / `ZOOM_MAX` (16) — 旧整数ズーム
 *   1..16 の主要な止まり所 (2 の倍数系列 + 中間の 1.5/3/6/12) を踏襲しつつ、
 *   全段を残すと ×1.1 の間隔よりアンカー間隔が狭くなる箇所が出るため間引いた
 *   (実機調整前提、consult 2026-09-29)。
 */
export const ZOOM_ANCHORS: readonly number[] = [
  ZOOM_FIT_MIN, // 0.1
  0.125, // 1/8
  0.2, // 1/5
  0.25, // 1/4
  1 / 3,
  0.5,
  0.75,
  ZOOM_MIN, // 1
  1.5,
  2,
  3,
  4,
  6,
  8,
  12,
  ZOOM_MAX, // 16
];

/** 浮動小数点誤差 (= 連続積算/除算の丸め) を吸収する比較許容差。 */
const ZOOM_STEP_EPSILON = 1e-9;

/**
 * `z` から `ZOOM_STEP_RATIO` 倍 (拡大) / ÷ (縮小) したときに `ZOOM_ANCHORS` を
 * またぐならアンカーちょうどへ、またがなければ `z * ratio` / `z / ratio` を
 * そのまま返す (決定2、CSP 方式)。
 *
 * `ZOOM_ANCHORS` は連続する値の比が常に `ZOOM_STEP_RATIO` (1.1) より大きい
 * (最小間隔 1.25 = 0.125/0.1) ので、1 手で 2 つ以上のアンカーをまたぐことは
 * 無い (= 最初に見つかったアンカーを返せば良く、複数候補の優先順位を考える
 * 必要が無い)。
 */
const stepTowardAnchor = (z: number, direction: 'up' | 'down'): number => {
  if (direction === 'up') {
    const raw = z * ZOOM_STEP_RATIO;
    // 昇順のまま探して、(z, raw] に入る最初 (= z に一番近い) のアンカーで止める。
    // 無ければ幾何級数どおりの raw。
    const anchor = ZOOM_ANCHORS.find(
      (a) => a > z + ZOOM_STEP_EPSILON && a <= raw + ZOOM_STEP_EPSILON,
    );
    return anchor ?? raw;
  }
  const raw = z / ZOOM_STEP_RATIO;
  // 降順 (= 配列を反転) に探して、[raw, z) に入る最初 (= z に一番近い) のアンカーで
  // 止める。無ければ幾何級数どおりの raw。
  const anchor = [...ZOOM_ANCHORS]
    .reverse()
    .find((a) => a < z - ZOOM_STEP_EPSILON && a >= raw - ZOOM_STEP_EPSILON);
  return anchor ?? raw;
};

/**
 * zoom + (拡大) のステップ (決定2、CSP 方式)。`z * ZOOM_STEP_RATIO` が既定の
 * 移動量、`ZOOM_ANCHORS` をまたぐ手はアンカーちょうどに止まる。上限
 * `ZOOM_MAX` で飽和。1x 以上/未満を区別しない (旧「1x 以上は整数ステップ」は
 * 不採用に変更、値の連続性は `ZOOM_ANCHORS` が担う)。
 *
 * ± ボタン / キーボード +/− / ホイールノッチが使う。
 */
export const zoomStepIn = (zoom: number): number => {
  const z = clampZoom(zoom);
  if (z >= ZOOM_MAX) return ZOOM_MAX;
  return clampZoom(stepTowardAnchor(z, 'up'));
};

/**
 * zoom − (縮小) のステップ (決定2、CSP 方式)。`z / ZOOM_STEP_RATIO` が既定の
 * 移動量、`ZOOM_ANCHORS` をまたぐ手はアンカーちょうどに止まる。下限
 * `ZOOM_FIT_MIN` で飽和。
 *
 * 旧仕様 (M1-a 当初の決定2 案) では 1x 未満は「フィット/ピンチで入るだけ・−
 * は no-op」であり、フィットで縮小したあと ± で戻せず詰んでいた (iPad FB の
 * 一因、consult 2026-09-29)。CSP 方式への差し替えでこの no-op は無くなり、
 * 1x 以上/未満を問わず − が常に効く (= 既存「整数ステップ」「1x 未満で − は
 * no-op」系テストは仕様変更として更新)。
 */
export const zoomStepOut = (zoom: number): number => {
  const z = clampZoom(zoom);
  if (z <= ZOOM_FIT_MIN) return ZOOM_FIT_MIN;
  return clampZoom(stepTowardAnchor(z, 'down'));
};

/**
 * fit-to-viewport の zoom 値を計算する純関数 (M1-a 入室時フィット / 後続の
 * フィットボタン・ピンチ下限)。
 *
 *   - canvas 全景が表示スロット (slotWidth × slotHeight、padding 控除後の
 *     内寸) に収まる最大の倍率 `min(slotW/canvasW, slotH/canvasH)` を返す。
 *   - canvas がスロットより**小さい** (fit >= 1) なら `ZOOM_MIN` (= 1、等倍)
 *     を返す — 入室時に勝手に拡大しない (PC の現行 1x 開始と同じ思想)。
 *   - 下限 `ZOOM_FIT_MIN` で飽和 (= 超巨大 canvas × 極小スロットでも 0.1x
 *     まで。全景に届かないケースは残りをスクロールで補う)。
 *   - 不正入力 (非有限 / 0 以下) は `ZOOM_MIN` へ倒す。
 */
export const computeFitZoom = (
  canvasWidth: number,
  canvasHeight: number,
  slotWidth: number,
  slotHeight: number,
): number => {
  if (
    !Number.isFinite(canvasWidth) ||
    !Number.isFinite(canvasHeight) ||
    canvasWidth <= 0 ||
    canvasHeight <= 0
  ) {
    return ZOOM_MIN;
  }
  if (
    !Number.isFinite(slotWidth) ||
    !Number.isFinite(slotHeight) ||
    slotWidth <= 0 ||
    slotHeight <= 0
  ) {
    return ZOOM_MIN;
  }
  const fit = Math.min(slotWidth / canvasWidth, slotHeight / canvasHeight);
  if (fit >= ZOOM_MIN) return ZOOM_MIN;
  return Math.max(fit, ZOOM_FIT_MIN);
};

/**
 * zoom 値の表示ラベル (例: `400%` / `133%` / `20%`)。CSP / Photoshop と同じ % 表記で
 * 整数 % に丸める (2026-09-29 iPad 実機 fb: `1.33x` のような小数表記は小さく読みづらい
 * → 小数点の無い % に)。UI (ModernToolRail の倍率表示) が使う。
 */
export const formatZoomLabel = (zoom: number): string => {
  const z = clampZoom(zoom);
  return `${Math.round(z * 100)}%`;
};

// ─── ドットバイドット表示 (2026-10-28, docs/_wip/canvas-dot-by-dot-2026-10-28.md) ───
//
// 従来は「キャンバス 1px = CSS 1px」で devicePixelRatio (dpr) を見ていなかった。
// dpr が割り切れない (1.25 / 1.5 / 1.75) と、1px が画面の 1 と 2 ドットにばらついて
// 線がガタガタに見える。dpr が 1 より大きく 2 未満のときだけ、ズーム 1x で
// 「キャンバス 1px = 画面 1 ドット」になるよう表示倍率を `zoom / dpr` にする。
// dpr <= 1 / dpr >= 2 (整数の 2 / 3 や Android の 2.625 を含む) は従来どおり。
//
// 用語: `zoom` = 利用者の見る倍率 (100% 表示・ステップ・フィットの単位)。
//       `displayScale` = キャンバス 1px が CSS 上で何 px になるか (全ての CSS 幾何
//       — transform / wrapper 寸法 / オーバーレイ配置 / ポインタ座標変換 — はこちら)。

/** ドットバイドット表示の設定。`'auto'` = dpr が (1, 2) のとき有効 / `'off'` = 常に従来どおり。 */
export type DotByDotMode = 'auto' | 'off';

export const DEFAULT_DOT_BY_DOT_MODE: DotByDotMode = 'auto';

/** dpr が (1, 2) の開区間か (= ドットバイドットを適用する範囲)。 */
export const isDotByDotDpr = (dpr: number): boolean => Number.isFinite(dpr) && dpr > 1 && dpr < 2;

/** zoom 1x のときの CSS 倍率の逆数 (= 割る数)。有効なら dpr、そうでなければ 1。 */
export const dotByDotDivisor = (dpr: number, mode: DotByDotMode): number =>
  mode === 'auto' && isDotByDotDpr(dpr) ? dpr : 1;

/** 利用者 zoom → CSS 表示倍率。 */
export const displayScaleFor = (zoom: number, dpr: number, mode: DotByDotMode): number =>
  zoom / dotByDotDivisor(dpr, mode);
